import { httpBatchLink, loggerLink } from '@trpc/client';
import type { NextPageContext } from 'next';
import { transformer } from './transformer';

function getBaseUrl() {
  if (typeof window !== 'undefined') {
    return '';
  }

  return `${process.env.NEXT_PUBLIC_BANZAI_CURRENT_SERVER}`;
}

/**
 * Extend `NextPageContext` with meta data that can be picked up by `responseMeta()` when server-side rendering
 */
export interface SSRContext extends NextPageContext {
  /**
   * Set HTTP Status code
   * @example
   * const utils = trpc.useUtils();
   * if (utils.ssrContext) {
   *   utils.ssrContext.status = 404;
   * }
   */
  status?: number;
}

export function createTRPCConfig(endpoint = '/api') {
  return {
    config({ ctx }: { ctx?: NextPageContext }) {
      /**
       * If you want to use SSR, you need to use the server's full URL
       * @link https://trpc.io/docs/v11/ssr
       */
      return {
        /**
         * @link https://trpc.io/docs/v11/client/links
         */
        links: [
          // adds pretty logs to your console in development and logs errors in production
          loggerLink({
            enabled: opts =>
              process.env.NODE_ENV === 'development' ||
              (opts.direction === 'down' && opts.result instanceof Error),
          }),
          httpBatchLink({
            url: `${getBaseUrl()}${endpoint}`,
            /**
             * Set custom request headers on every request from tRPC
             * @link https://trpc.io/docs/v11/ssr
             */
            headers() {
              if (!ctx?.req?.headers) {
                return {};
              }
              // To use SSR properly, you need to forward the client's headers to the server
              // This is so you can pass through things like cookies when we're server-side rendering

              const {
                // If you're using Node 18 before 18.15.0, omit the "connection" header
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                connection: _connection,
                ...headers
              } = ctx.req.headers;
              return headers;
            },
            /**
             * @link https://trpc.io/docs/v11/data-transformers
             */
            transformer,
          }),
        ],
        /**
         * @link https://tanstack.com/query/v5/docs/reference/QueryClient
         */
        // queryClientConfig: { defaultOptions: { queries: { staleTime: 60 } } },
      };
    },
    /**
     * @link https://trpc.io/docs/v11/ssr
     */
    ssr: false,
    /**
     * @link https://trpc.io/docs/v11/data-transformers
     */
    transformer,
  } as any;
}
